import CustomTooltip from '../../../shared/CustomTooltip';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
    Box,
    Button,
    FormHelperText,
    Grid,
    InputLabel,
    List,
    ListItem,
    Paper,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    useEditUserMutation,
    useGetAllReservationsQuery,
    useGetTeamListQuery,
    useGetUserListQuery,
} from '../../../../app/Slices/SevenDayApiSlice';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import UserRole from '../../../../shared/AuthRoles';
import Modal from '../../../shared/Modal';
import toast from '../../../shared/Toast';
import { Media, User } from '../../../types/user';
import EditProfilePicture from '../../Profile/Form/EditProfilePicture';
import { personalAccessTooltipContent } from '../../Profile/ProfileReadOnlyField';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import FormTextField from '../../../shared/FormTextField';
import {
    AdminRole,
    ContractorRole,
    GuestRole,
    OwnerRole,
} from '../../../enums/users';
import FormSelect from '../../../shared/FormSelect';
import { EditSchema } from './schema';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import FormAutocomplete from '../../../shared/FormAutocomplete';
import { Add } from '@mui/icons-material';
import UserReservationsModal from '../UserReservations/UserReservationsModal';
import EmbeddedManagedUserEditShell from './EmbeddedManagedUserEditShell';

export interface Teams {
    id: number;
    name: string;
}

export default function EditUserForm({
    user,
    embedded = false,
    embeddedEditScope,
    onEmbeddedCancel,
    onEmbeddedSaved,
    embeddedCanSendInvite,
    onEmbeddedSendOrResendInvite,
}: {
    user: any;
    embedded?: boolean;
    /** When `embedded`, limit visible fields to one section (profile-style). */
    embeddedEditScope?: 'personal' | 'login';
    onEmbeddedCancel?: () => void;
    onEmbeddedSaved?: () => void;
    embeddedCanSendInvite?: boolean;
    onEmbeddedSendOrResendInvite?: () => void;
}) {
    const navigate = useNavigate();
    const location = useLocation();
    const loggedInUser = UserRole();
    const isIpad = useMediaQuery('(max-width: 1200px)');
    const isMobile = useMediaQuery('(max-width: 770px)');

    const [reservationsOpen, setReservationsOpen] = useState(false);

    type FormValues = z.infer<typeof EditSchema>;

    const methods = useForm<FormValues>({
        defaultValues: {
            email: '',
            firstName: '',
            lastName: '',
            phone: '',
            role: 'user',
            isActive: false,
            department: null,
            vehiclePlate: '',
        },
        resolver: zodResolver(EditSchema),
    });

    const {
        control,
        handleSubmit,
        setValue,
        reset,
        setError,
        watch,
        getValues,
        formState: { isSubmitting, isDirty, dirtyFields },
    } = methods;

    const userDisabled =
        dirtyFields.isActive && getValues('isActive') === false;
    const { data: upcomingReservations } = useGetAllReservationsQuery(
        {
            bookerIds: [user?.id],
            status: ['approved', 'pending'],
            startDate: 'future',
        },
        { skip: !userDisabled },
    );

    const getRole = (user: any) => {
        if (user.viewOnly) return 'viewOnlyUser';

        if (
            user.role === OwnerRole.User ||
            user.role === GuestRole.User ||
            user.role === ContractorRole.User
        )
            return 'user';

        if (user.role === GuestRole.DepartmentAdmin) return 'teamAdmin';

        return 'admin';
    };

    useEffect(() => {
        if (user) {
            const role = getRole(user);

            reset({
                ...getValues(),
                ...user,
                role,
            });
        }
    }, [user]);

    const [editUser] = useEditUserMutation();

    const url = loggedInUser?.role.includes('owner')
        ? `/owners/${loggedInUser?.company}/users`
        : loggedInUser?.role.includes('guest')
          ? `/guests/${loggedInUser?.company}/users`
          : loggedInUser?.role.includes('contractor')
            ? `/contractors/${loggedInUser?.company}/users`
            : `/7-day/users`;

    const { data: getUsers } = useGetUserListQuery(url, {
        skip: !loggedInUser,
    });

    const department = useWatch({ name: 'department', control });

    const values = watch();

    //Get List of Teams for dropdown selection

    const [teamList, setTeamList] = useState<any>([]);
    const { data: getTeams } = useGetTeamListQuery(loggedInUser?.company);
    const teams: Teams[] = getTeams ?? [];

    // Modal for confirming change of team if department_admin
    const [openModal, setOpen] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const [modalComponent, setModalComponent] = useState('');

    const handleCloseModal = () => {
        setOpen(false);
    };
    useEffect(() => {
        const allTeams: any = teams?.map((val: any) => {
            return val?.owner?.name
                ? {
                      value: val?.id,
                      label:
                          val?.name?.charAt(0)?.toUpperCase() +
                          val?.name.slice(1),
                  }
                : {
                      value: val?.id,
                      label:
                          val?.name?.charAt(0)?.toUpperCase() +
                          val?.name.slice(1),
                  };
        });

        const filterOutDuplicates = allTeams?.filter(
            (value: any, index: any, self: any) =>
                index ===
                self.findIndex(
                    (t: any) =>
                        t?.label === value?.label && t?.label === value?.label,
                ),
        );

        setTeamList(filterOutDuplicates);
    }, [getTeams]);

    useEffect(() => {
        if (department && user) {
            if (department.id !== user.department?.id) {
                if (
                    loggedInUser?.role === 'guest_admin' &&
                    user?.role === 'guest_department_admin'
                ) {
                    //execute line
                    setOpen(true);

                    setModalMessage('Warning');
                    setModalComponent('Team change');
                }
            }
        }
    }, [department, user]);

    const handlePhoto = (photo: Media) => {
        setValue('profilePicture', photo, { shouldDirty: true });
    };

    const handleDuplicateEmail = (data: FormValues) => {
        const duplicate = getUsers
            .filter(({ id }: User) => id !== user.id)
            .find((user: User) => user.email === data.email);

        if (duplicate) {
            setError('email', {
                message: 'A user with this email already exists',
            });
            return true;
        }

        return false;
    };

    const handleRole = (
        role: 'teamAdmin' | 'user' | 'admin' | 'viewOnlyUser',
    ) => {
        if (!user.role) return AdminRole.Admin;

        if (user?.role.includes('owner')) {
            if (role === 'user' || role === 'viewOnlyUser')
                return OwnerRole.User;

            return OwnerRole.Admin;
        }

        if (user?.role.includes('guest')) {
            if (role === 'user' || role === 'viewOnlyUser')
                return GuestRole.User;

            if (role === 'teamAdmin') return GuestRole.DepartmentAdmin;

            return GuestRole.Admin;
        }

        if (user.role.includes('contractor')) {
            if (role === 'user') return ContractorRole.User;

            return ContractorRole.Admin;
        }
    };

    const RoleOptions =
        loggedInUser?.role === 'admin'
            ? [
                  {
                      value: 'admin',
                      label: 'Admin',
                  },
              ]
            : loggedInUser.role.includes('guest')
              ? loggedInUser?.role === 'guest_department_admin'
                  ? [
                        {
                            value: 'user',
                            label: 'User',
                            isDefault: true,
                        },
                        {
                            value: 'viewOnlyUser',
                            label: 'View Only User',
                        },
                        ...(user?.role === 'guest_department_admin'
                            ? [
                                  {
                                      value: 'teamAdmin',
                                      label: 'Team Admin',
                                  },
                              ]
                            : []),
                    ]
                  : [
                        {
                            value: 'admin',
                            label: 'Company Admin',
                        },
                        {
                            value: 'teamAdmin',
                            label: 'Team Admin',
                        },
                        {
                            value: 'user',
                            label: 'User',
                            isDefault: true,
                        },
                        {
                            value: 'viewOnlyUser',
                            label: 'View Only User',
                        },
                    ]
              : loggedInUser?.role.includes('owner')
                ? [
                      {
                          value: 'admin',
                          label: 'Admin',
                      },
                      {
                          value: 'user',
                          label: 'User',
                          isDefault: true,
                      },
                      {
                          value: 'viewOnlyUser',
                          label: 'View Only User',
                      },
                  ]
                : loggedInUser?.role.includes('contractor')
                  ? [
                        {
                            value: 'admin',
                            label: 'Admin',
                        },
                        {
                            value: 'user',
                            label: 'User',
                        },
                    ]
                  : [];

    const onSubmit = async (data: FormValues) => {
        if (userDisabled && (upcomingReservations || []).length) {
            setReservationsOpen(true);
            return;
        }

        if (handleDuplicateEmail(data)) return;

        const URL = `${url}/${user?.id}`;

        try {
            const { role, ...rest } = data;
            const request = {
                ...rest,
                role: handleRole(role),
                viewOnly: role === 'viewOnlyUser',
            };

            const response = await editUser([URL, request]).unwrap();

            toast.success(`${response.firstName} has been edited`);

            const profilePath = `/users/${response.firstName
                .replace(/ /g, '')
                .toLowerCase()}/${response.id}`;
            if (embedded) {
                navigate(profilePath, {
                    replace: true,
                    state: location.state,
                });
                onEmbeddedSaved?.();
            } else {
                navigate(profilePath, { state: location.state });
            }
        } catch (error) {
            toast.error(`Failed to edit user: ${user.firstName}`);
        }
    };

    return (
        <FormProvider {...methods}>
            <form
                id={embedded ? 'managed-user-edit-form' : undefined}
                onSubmit={handleSubmit(onSubmit)}
            >
                {embedded && embeddedEditScope ? (
                    <EmbeddedManagedUserEditShell
                        scope={embeddedEditScope}
                        onCancel={() => onEmbeddedCancel?.()}
                        user={user}
                        roleOptions={RoleOptions}
                        teamList={teamList}
                        invite={user?.invite}
                        canSendInvite={Boolean(embeddedCanSendInvite)}
                        onSendOrResendInvite={
                            onEmbeddedSendOrResendInvite ?? (() => {})
                        }
                        loggedInRole={loggedInUser?.role}
                        viewOnly={Boolean(user?.viewOnly)}
                    />
                ) : (
                    <Grid
                        sx={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            paddingTop: embedded
                                ? '8px'
                                : isIpad
                                  ? '0px'
                                  : '30px',
                            height: 'auto',
                        }}
                    >
                        <Grid
                            sx={{
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                height: 'auto',
                                paddingTop: isIpad ? '20px' : '',
                            }}
                        >
                            {!loggedInUser?.role?.includes('company') &&
                                loggedInUser?.role !== 'admin' &&
                                !embedded && (
                                    <EditProfilePicture
                                        user={user}
                                        onSubmit={handlePhoto}
                                        message={false}
                                    />
                                )}

                            {!embedded ? (
                                <Typography
                                    component='h2'
                                    color='text.primary'
                                    sx={{
                                        fontWeight: 'bold',
                                        py: '30px',
                                    }}
                                >
                                    User
                                </Typography>
                            ) : null}
                            <Grid
                                container
                                columnSpacing='50px'
                                rowSpacing='10px'
                            >
                                <Grid item container xs={12} md={6}>
                                    <Grid item xs={12} lg={5}>
                                        <InputLabel
                                            required={values.firstName === ''}
                                        >
                                            First Name
                                        </InputLabel>
                                    </Grid>
                                    <Grid item xs={12} lg={7}>
                                        <FormTextField
                                            name='firstName'
                                            control={control}
                                            placeholder='John'
                                        />
                                    </Grid>
                                </Grid>
                                <Grid item container xs={12} md={6}>
                                    <Grid item xs={12} lg={5}>
                                        <InputLabel
                                            required={values.lastName === ''}
                                        >
                                            Surname
                                        </InputLabel>
                                    </Grid>
                                    <Grid item xs={12} lg={7}>
                                        <FormTextField
                                            name='lastName'
                                            control={control}
                                            placeholder='Smith'
                                        />
                                    </Grid>
                                </Grid>
                                <Grid item container xs={12} md={6}>
                                    <Grid item xs={12} lg={5}>
                                        <InputLabel
                                            required={values.phone === ''}
                                        >
                                            Phone
                                        </InputLabel>
                                    </Grid>
                                    <Grid item xs={12} lg={7}>
                                        <FormTextField
                                            name='phone'
                                            control={control}
                                            placeholder='0414789456'
                                            type='tel'
                                        />
                                    </Grid>
                                </Grid>
                                {loggedInUser?.role.includes('guest') && (
                                    <Grid item container xs={12} md={6}>
                                        <Grid item xs={12} lg={5}>
                                            <InputLabel
                                                required={
                                                    values.role ===
                                                        'teamAdmin' &&
                                                    !values.department?.id
                                                }
                                            >
                                                Team{' '}
                                                <CustomTooltip
                                                    title={
                                                        <Typography>
                                                            <List>
                                                                By selecting*
                                                                the users team,
                                                                they will appear
                                                                in the 'Team
                                                                Managers'
                                                                profile. Team
                                                                Managers will
                                                                have access to
                                                                view and manage
                                                                users that
                                                                belong to their
                                                                team as well as
                                                                view and edit
                                                                any reservations
                                                                that belong to
                                                                them.
                                                            </List>
                                                            <List>
                                                                * Only a company
                                                                admin can change
                                                                a users Team.
                                                            </List>
                                                        </Typography>
                                                    }
                                                    placement='right'
                                                >
                                                    <HelpOutlineIcon
                                                        sx={{
                                                            color: 'text.primary',
                                                            fontSize: '16px',
                                                        }}
                                                    />
                                                </CustomTooltip>
                                            </InputLabel>
                                        </Grid>
                                        <Grid item xs={12} lg={7}>
                                            <FormAutocomplete
                                                name='department.id'
                                                control={control}
                                                options={teamList}
                                                disabled={
                                                    loggedInUser?.role ===
                                                    GuestRole.DepartmentAdmin
                                                }
                                                disableClearable={undefined}
                                                PaperComponent={({
                                                    children,
                                                }) => {
                                                    return (
                                                        <Paper>
                                                            <Button
                                                                fullWidth
                                                                size='large'
                                                                sx={{
                                                                    justifyContent:
                                                                        'flex-start',
                                                                    pl: 2,
                                                                    mt: 1,
                                                                }}
                                                                startIcon={
                                                                    <Add />
                                                                }
                                                                onMouseDown={(
                                                                    e,
                                                                ) => {
                                                                    e.stopPropagation();
                                                                    window.open(
                                                                        '/newTeam',
                                                                        '_blank',
                                                                    );
                                                                }}
                                                            >
                                                                Create New Team
                                                            </Button>
                                                            {children}
                                                        </Paper>
                                                    );
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                )}
                                <Grid item container xs={12} md={6}>
                                    <Grid item xs={12} lg={5}>
                                        <InputLabel>
                                            Access{' '}
                                            <CustomTooltip
                                                title={personalAccessTooltipContent(
                                                    loggedInUser,
                                                )}
                                                placement='right'
                                            >
                                                <HelpOutlineIcon
                                                    sx={{
                                                        color: 'text.primary',
                                                        fontSize: '16px',
                                                    }}
                                                />
                                            </CustomTooltip>
                                        </InputLabel>{' '}
                                    </Grid>
                                    <Grid item xs={12} lg={7}>
                                        <FormSelect
                                            name='role'
                                            control={control}
                                            options={RoleOptions}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid item container xs={12} md={6}>
                                    <Grid item xs={12} lg={5}>
                                        <InputLabel>
                                            Status{' '}
                                            <CustomTooltip
                                                title={
                                                    <Typography>
                                                        <List>
                                                            <ListItem>
                                                                {' '}
                                                                Enabled: Users
                                                                can access the
                                                                site and perform
                                                                actions based on
                                                                their level of
                                                                access
                                                            </ListItem>
                                                            <ListItem>
                                                                {' '}
                                                                Disabled: Users
                                                                will not be able
                                                                to access the
                                                                site
                                                            </ListItem>
                                                        </List>
                                                    </Typography>
                                                }
                                                placement='right'
                                            >
                                                <HelpOutlineIcon
                                                    sx={{
                                                        color: 'text.primary',
                                                        fontSize: '16px',
                                                    }}
                                                />
                                            </CustomTooltip>
                                        </InputLabel>{' '}
                                    </Grid>
                                    <Grid item xs={12} lg={7}>
                                        <FormSelect
                                            name='isActive'
                                            control={control}
                                            options={[
                                                {
                                                    value: true,
                                                    label: 'Enabled',
                                                },
                                                {
                                                    value: false,
                                                    label: 'Disabled',
                                                },
                                            ]}
                                        />
                                    </Grid>
                                </Grid>
                                {loggedInUser?.role !== AdminRole.Admin && (
                                    <>
                                        <Grid item container xs={12} md={6}>
                                            <Grid item xs={12} lg={5}>
                                                <InputLabel>
                                                    Vehicle Plate{' '}
                                                    <CustomTooltip
                                                        title={
                                                            <Typography>
                                                                Some experiences
                                                                might need plate
                                                                numbers to give
                                                                vehicles access
                                                                to the location.
                                                            </Typography>
                                                        }
                                                        placement='right'
                                                    >
                                                        <HelpOutlineIcon
                                                            sx={{
                                                                color: 'text.primary',
                                                                fontSize:
                                                                    '16px',
                                                            }}
                                                        />
                                                    </CustomTooltip>
                                                </InputLabel>
                                            </Grid>
                                            <Grid item xs={12} lg={7}>
                                                <FormTextField
                                                    name='vehiclePlate'
                                                    control={control}
                                                />
                                            </Grid>
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        </Grid>
                        {!embedded ? (
                            <Typography
                                component='h2'
                                color='text.primary'
                                sx={{
                                    fontWeight: 'bold',
                                    py: '30px',
                                }}
                            >
                                Login Details
                            </Typography>
                        ) : (
                            <Typography
                                component='h3'
                                color='text.primary'
                                sx={{
                                    fontWeight: 'bold',
                                    py: 2,
                                }}
                            >
                                Login details
                            </Typography>
                        )}
                        <Grid container columnSpacing='50px' rowSpacing='10px'>
                            <Grid item container xs={12} md={6}>
                                <Grid item xs={12} lg={5}>
                                    <InputLabel required={values.email === ''}>
                                        Email
                                    </InputLabel>
                                </Grid>
                                <Grid item xs={12} lg={7}>
                                    <FormTextField
                                        name='email'
                                        control={control}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Box
                            sx={{
                                display: 'flex',
                                py: '50px',
                                justifyContent:
                                    isMobile || isIpad
                                        ? 'flex-end'
                                        : 'space-between',
                            }}
                        >
                            {embedded ? (
                                !(isMobile || isIpad) ? (
                                    <CustomButton
                                        onClick={() => onEmbeddedCancel?.()}
                                        color='primary'
                                        sx={{ ml: -1 }}
                                    >
                                        <Typography sx={{ fontSize: '12px' }}>
                                            Cancel
                                        </Typography>
                                    </CustomButton>
                                ) : null
                            ) : (
                                !(isMobile || isIpad) && (
                                    <CustomButton
                                        onClick={() => navigate(`/users`)}
                                        color='primary'
                                        sx={{ ml: -1 }}
                                        startIcon={<ArrowBackIcon />}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: '12px',
                                            }}
                                        >
                                            Back to users
                                        </Typography>
                                    </CustomButton>
                                )
                            )}
                            <Stack
                                direction='row'
                                sx={{ gap: 1, alignItems: 'center' }}
                            >
                                {!isDirty && (
                                    <FormHelperText>
                                        *No changes have been made
                                    </FormHelperText>
                                )}
                                {embedded && (isMobile || isIpad) ? (
                                    <Button
                                        variant='outlined'
                                        type='button'
                                        onClick={() => onEmbeddedCancel?.()}
                                        disabled={isSubmitting}
                                    >
                                        Cancel
                                    </Button>
                                ) : null}
                                <Button
                                    variant='contained'
                                    type='submit'
                                    disabled={!isDirty || isSubmitting}
                                >
                                    Save
                                </Button>
                            </Stack>
                        </Box>
                    </Grid>
                )}
                <Modal
                    open={openModal}
                    onClose={handleCloseModal}
                    message={modalMessage}
                    component={modalComponent}
                />
                {user && upcomingReservations && (
                    <UserReservationsModal
                        user={user}
                        upcomingReservations={upcomingReservations}
                        open={reservationsOpen}
                        onClose={() => setReservationsOpen(false)}
                    />
                )}
            </form>
        </FormProvider>
    );
}
